import {
	CreateProjectRequest,
	InstructionRequest,
	isServerEvent,
	NavigationRequest,
	ResponseCreateEvent,
	SearchRequest,
	ServerEvent,
	SessionUpdateEvent,
} from './ai-assistant.types';
import { AiToolsControlService } from './ai-tools-control.service';
import { MemoryManager } from './memory.manager';

export class EventManager {
	private readonly eventHandlers = new Map<string, (event: MessageEvent) => void>();
	private dataChannel: RTCDataChannel | null = null;

	constructor(
		private readonly toolsControl: AiToolsControlService,
		private readonly memoryManager: MemoryManager,
		private readonly onInstructionNeeded: (instruction: string) => void,
	) {}

	/**
	 * Set the data channel for event handling
	 */
	public setDataChannel(dataChannel: RTCDataChannel): void {
		this.dataChannel = dataChannel;
	}

	/**
	 * Add typed event handler for session initialization
	 */
	public addSessionHandler(onSessionCreated: () => void): void {
		const handler = this.createTypedEventHandler((event: ServerEvent) => {
			if (event.type === 'session.created') {
				onSessionCreated();
			}
		});

		this.addHandler('session', handler);
	}

	/**
	 * Add main event handler for AI communication
	 */
	public addMainEventHandler(): void {
		const handler = this.createTypedEventHandler((event: ServerEvent) => {
			this.handleServerEvent(event);
		});

		this.addHandler('main', handler);
	}

	/**
	 * Remove specific event handler
	 */
	public removeHandler(name: string): void {
		const handler = this.eventHandlers.get(name);
		if (handler && this.dataChannel) {
			this.dataChannel.removeEventListener('message', handler);
			this.eventHandlers.delete(name);
		}
	}

	/**
	 * Clean up all event handlers
	 */
	public cleanup(): void {
		for (const [name] of this.eventHandlers) {
			this.removeHandler(name);
		}
		this.dataChannel = null;
	}

	/**
	 * Send data through the channel
	 */
	public sendData(data: SessionUpdateEvent | ResponseCreateEvent): void {
		if (this.dataChannel && this.dataChannel.readyState === 'open') {
			this.dataChannel.send(JSON.stringify(data));
		} else {
			console.warn('Data channel not ready for sending data');
		}
	}

	/**
	 * Add event handler to data channel
	 */
	private addHandler(name: string, handler: (event: MessageEvent) => void): void {
		this.removeHandler(name);

		this.eventHandlers.set(name, handler);
		this.dataChannel?.addEventListener('message', handler);
	}

	/**
	 * Create typed event handler with error handling
	 */
	private createTypedEventHandler(processor: (event: ServerEvent) => void) {
		return (event: MessageEvent): void => {
			try {
				const data = JSON.parse(event.data);
				if (isServerEvent(data)) {
					processor(data);
				} else {
					console.warn('Received invalid server event:', data);
				}
			} catch (error) {
				console.error('Failed to parse server event:', error, event.data);
			}
		};
	}

	/**
	 * Handle different types of server events
	 */
	private handleServerEvent(event: ServerEvent): void {
		switch (event.type) {
			case 'transcript.text.delta':
				break;

			case 'response.done':
				break;

			case 'response.function_call_arguments.done':
				this.handleFunctionCall(event);
				break;

			case 'conversation.item.input_audio_transcription.completed':
				this.handleUserTranscription(event);
				break;

			case 'response.audio_transcript.done':
				this.handleAssistantTranscription(event);
				break;

			default:
				break;
		}
	}

	/**
	 * Handle function call from AI assistant
	 */
	private handleFunctionCall(event: ServerEvent): void {
		if (!event.name || !event.arguments) {
			console.warn('Invalid function call event:', event);
			return;
		}

		try {
			const args = JSON.parse(event.arguments);

			switch (event.name) {
				case 'navigation':
					this.handleNavigation(args);
					break;

				case 'create_project':
					this.handleCreateProject(args);
					break;

				case 'search_item':
					this.handleSearchItem(args);
					break;

				case 'need_instruction':
					this.handleNeedInstruction(args);
					break;

				default:
					break;
			}
		} catch (error) {
			console.error('Failed to parse function arguments:', error, event.arguments);
		}
	}

	/**
	 * Handle navigation function call
	 */
	private handleNavigation(args: NavigationRequest): void {
		if (!args.route) {
			console.warn('Navigation call missing route:', args);
			return;
		}

		this.toolsControl.navigation(args).catch(error => {
			console.error('Navigation failed:', error);
		});
	}

	/**
	 * Handle create project function call
	 */
	private handleCreateProject(args: CreateProjectRequest): void {
		if (!args.projectName) {
			console.warn('Create project call missing project name:', args);
			return;
		}

		this.toolsControl.createProject(args.projectName).catch(error => {
			console.error('Project creation failed:', error);
		});
	}

	/**
	 * Handle search function call
	 */
	private handleSearchItem(args: SearchRequest): void {
		if (!args.query) {
			console.warn('Search call missing query:', args);
			return;
		}

		this.toolsControl.search(args.query).catch(error => {
			console.error('Search failed:', error);
		});
	}

	/**
	 * Handle instruction request
	 */
	private handleNeedInstruction(args: InstructionRequest): void {
		if (!args.instruction) {
			console.warn('Instruction call missing instruction name:', args);
			return;
		}

		this.onInstructionNeeded(args.instruction);
	}

	/**
	 * Handle user transcription completion
	 */
	private handleUserTranscription(event: ServerEvent): void {
		if (event.transcript) {
			this.memoryManager.commitMemory('user', event.transcript);
		}
	}

	/**
	 * Handle assistant transcription completion
	 */
	private handleAssistantTranscription(event: ServerEvent): void {
		if (event.transcript) {
			this.memoryManager.commitMemory('assistant', event.transcript);
		}
	}
}
